/**
 * A hook to allow Elevio article feedback to be implemented easily.
 *
 * An initial example of the usage of this is provided in the starter kits, it is recommend you customise your feedback components from that base rather than try to understand everything this hook does.
 *
 * @module
 */
import { useState } from 'react';
import { useSelector, useDispatch } from '../actions/types';
import { Actions as ArticleFeedbackActions } from '../actions/articleFeedback';
import { getFeedbackEmailFieldHidden, getFeedbackEnabled, getUser, } from '../utils/globals';
import { getOnChangeValue } from '../utils';
import { useTranslation } from './useTranslation';
import { useArticleFragment } from './useArticleFragment';
import '../i18n';
/**
 * # useArticleFeedback
 *
 * A hook to allow Elevio article feedback to be implemented easily.
 */
export function useArticleFeedback(_articleId) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = getUser();
    const emailFieldIsHidden = getFeedbackEmailFieldHidden() || !!user.email;
    const initialValues = {
        email: emailFieldIsHidden ? undefined : '',
        feedback: '',
    };
    const [values, setValues] = useState(initialValues);
    const article = useArticleFragment({ articleId: _articleId });
    const [showAnonymousWarning, setShowAnonymousWarning] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    if (!article)
        throw new Error('Unable to find article for ArticleFeedback');
    const articleId = article.id;
    const handlePositive = () => dispatch(ArticleFeedbackActions.feedbackReactionPressed(articleId, article.title, true));
    const handleNegative = () => dispatch(ArticleFeedbackActions.feedbackReactionPressed(articleId, article.title, false));
    const handleFeedbackSkipped = () => dispatch(ArticleFeedbackActions.negativeFeedbackSkipped(articleId));
    const handleFeedbackSubmitted = (formData) => dispatch(ArticleFeedbackActions.negativeFeedbackSubmit(articleId, article.title, formData));
    const isLoading = useSelector((state) => state.articleFeedback.isLoading);
    const isFeedbackFormSubmitting = useSelector((state) => state.articleFeedback.isFormSubmitting);
    const stage = useSelector((state) => state.articleFeedback.stage);
    const handleMessageChange = (eventOrValue) => {
        const val = getOnChangeValue(eventOrValue);
        setValues({ ...values, feedback: val });
    };
    const handleEmailChange = (eventOrValue) => {
        const val = getOnChangeValue(eventOrValue);
        setValues({ ...values, email: val });
    };
    const messageError = !values.feedback ? t('required', 'Required') : null;
    const emailError = values.email && !values.email.match(/^.+@.+\..+$/)
        ? t('invalid_email', 'Invalid email')
        : null;
    const onSubmit = (event) => {
        if (event)
            event.preventDefault();
        setHasSubmitted(true);
        // Bail and let the user fix the validation issues.
        if (messageError || emailError)
            return;
        // Show the anon submit message, if no email and field is shown.
        if (!showAnonymousWarning && !values.email && !emailFieldIsHidden) {
            setShowAnonymousWarning(true);
            return;
        }
        handleFeedbackSubmitted(values);
    };
    return {
        stage,
        isFeedbackEnabled: getFeedbackEnabled(),
        isLoading,
        isFeedbackFormSubmitting,
        values,
        emailFieldIsHidden,
        showAnonymousWarning,
        hasSubmitted,
        messageError: hasSubmitted ? messageError : null,
        emailError: hasSubmitted ? emailError : null,
        onPositiveReaction: handlePositive,
        onNegativeReaction: handleNegative,
        onFeedbackSkipped: handleFeedbackSkipped,
        handleEmailChange,
        handleMessageChange,
        onSubmit,
    };
}
//# sourceMappingURL=useArticleFeedback.js.map